import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  
  // Simplified Impersonation State
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalToken, setOriginalToken] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);

  useEffect(() => {
    // Check for active impersonation
    const savedOriginalToken = localStorage.getItem('vett_original_token');
    const savedOriginalUser = localStorage.getItem('vett_original_user');
    
    if (savedOriginalToken && savedOriginalUser) {
      setOriginalToken(savedOriginalToken);
      setOriginalUser(JSON.parse(savedOriginalUser));
      setIsImpersonating(true);
    }
    
    const token = localStorage.getItem('vett_token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setEmailVerified(response.data.email_verified || false);
    } catch (error) {
      localStorage.removeItem('vett_token');
      localStorage.removeItem('vett_original_token');
      localStorage.removeItem('vett_original_user');
      setIsImpersonating(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshVerificationStatus = useCallback(async () => {
    const token = localStorage.getItem('vett_token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/auth/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailVerified(response.data.email_verified || false);
      if (response.data.email_verified && user) {
        setUser(prev => ({ ...prev, email_verified: true }));
      }
    } catch (error) {
      console.error('Failed to refresh verification status:', error);
    }
  }, [user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token, user } = response.data;
    localStorage.setItem('vett_token', token);
    setUser(user);
    setEmailVerified(user.email_verified || false);
    return user;
  };

  const signup = async (email, password, accountType, termsAccepted = false) => {
    const response = await axios.post(`${API_URL}/auth/signup`, { 
      email, 
      password, 
      account_type: accountType,
      terms_accepted: termsAccepted
    });
    const { token, user } = response.data;
    localStorage.setItem('vett_token', token);
    setUser(user);
    setEmailVerified(false);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('vett_token');
    localStorage.removeItem('vett_original_token');
    localStorage.removeItem('vett_original_user');
    setUser(null);
    setEmailVerified(false);
    setIsImpersonating(false);
    setOriginalToken(null);
    setOriginalUser(null);
  };

  /**
   * ONE-CLICK IMPERSONATION: loginAsUser(userId)
   * Instantly switches to viewing the app as another user.
   */
  const loginAsUser = async (userId) => {
    const currentToken = localStorage.getItem('vett_token');
    
    try {
      // Call impersonation API
      const response = await axios.post(
        `${API_URL}/admin/impersonate/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      
      const { impersonation_token, target_user } = response.data;
      
      // Save original session (only if not already impersonating)
      if (!isImpersonating) {
        localStorage.setItem('vett_original_token', currentToken);
        localStorage.setItem('vett_original_user', JSON.stringify(user));
        setOriginalToken(currentToken);
        setOriginalUser(user);
      }
      
      // Swap to impersonation token
      localStorage.setItem('vett_token', impersonation_token);
      setIsImpersonating(true);
      
      // Fetch the impersonated user's full data
      await fetchUser(impersonation_token);
      
      return target_user;
    } catch (error) {
      console.error('Impersonation failed:', error);
      throw error;
    }
  };

  /**
   * EXIT GOD MODE: Instantly restore original session
   */
  const exitGodMode = () => {
    const savedToken = localStorage.getItem('vett_original_token');
    const savedUser = localStorage.getItem('vett_original_user');
    
    if (savedToken && savedUser) {
      // Restore original session
      localStorage.setItem('vett_token', savedToken);
      setUser(JSON.parse(savedUser));
      
      // Clear impersonation state
      localStorage.removeItem('vett_original_token');
      localStorage.removeItem('vett_original_user');
      setIsImpersonating(false);
      setOriginalToken(null);
      setOriginalUser(null);
      
      return true;
    }
    return false;
  };

  const getToken = () => localStorage.getItem('vett_token');

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      emailVerified,
      login, 
      signup, 
      logout, 
      getToken,
      refreshVerificationStatus,
      // God Mode / Impersonation
      isImpersonating,
      originalUser,
      loginAsUser,
      exitGodMode
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
